import {
    Component,
    Input,
    ViewChild,
    AfterViewInit,
    OnDestroy,
    ElementRef,
    Output,
    EventEmitter,
    OnInit,
} from '@angular/core';
import { Chart } from 'chart.js';
import 'chartjs-adapter-luxon';
import { DateTime } from 'luxon';
import { Observable, Subject, BehaviorSubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { COLOR_CONSTANTS } from '../../shared/scss-variables.generated';

export interface GaugeData {
    values: number[];
    text: string;
    subtext: string;
    color: string;
}

@Component({
    selector: 'app-gauge',
    templateUrl: './template.html',
    styleUrls: ['./style.scss'],
})
export class GaugeComponent implements OnInit, OnDestroy {
    @Input() data$: Observable<GaugeData>;
    @Input() data: GaugeData;
    @Input() labels: string[] = [];
    @Input() height: number;
    @Input() textSize = 20;

    @ViewChild('canvas', { static: true }) canvas: ElementRef<HTMLCanvasElement>;

    private chart: Chart;
    private ctx;
    private rawData = [];
    private graphData = {
        labels: this.labels,
        datasets: [
            {
                labels: this.labels,
                backgroundColor: [COLOR_CONSTANTS['$river-blue'], COLOR_CONSTANTS['$all-white']],
                data: [],
            },
        ],
    };

    private unsub: Subject<void> = new Subject();

    constructor(private el: ElementRef) {}

    ngOnInit() {
        this.ctx = this.canvas.nativeElement.getContext('2d');

        // round corners
        Chart.pluginService.register({
            afterUpdate: function (chart) {
                // @ts-ignore
                if (chart.config.options.elements.arc.roundedCornersFor !== undefined) {
                    // @ts-ignore
                    const arc = chart.getDatasetMeta(0).data[chart.config.options.elements.arc.roundedCornersFor];
                    // @ts-ignore
                    arc.round = {
                        x: (chart.chartArea.left + chart.chartArea.right) / 2,
                        y: (chart.chartArea.top + chart.chartArea.bottom) / 2,
                        // @ts-ignore
                        radius: (chart.outerRadius + chart.innerRadius) / 2,
                        // @ts-ignore
                        thickness: (chart.outerRadius - chart.innerRadius) / 2 - 1,
                        backgroundColor: arc._model.backgroundColor,
                    };
                }
            },

            afterDraw: function (chart) {
                // @ts-ignore
                if (chart.config.options.elements.arc.roundedCornersFor !== undefined) {
                    // @ts-ignore
                    const ctx = chart.chart.ctx;
                    // @ts-ignore
                    const arc = chart.getDatasetMeta(0).data[chart.config.options.elements.arc.roundedCornersFor];
                    // @ts-ignore
                    const startAngle = Math.PI / 2 - arc._view.startAngle;
                    // @ts-ignore
                    const endAngle = Math.PI / 2 - arc._view.endAngle;

                    ctx.save();
                    // @ts-ignore
                    ctx.translate(arc.round.x, arc.round.y);
                    // @ts-ignore
                    ctx.fillStyle = chart.config.options.elements.arc.color;
                    ctx.beginPath();
                    ctx.arc(
                        // @ts-ignore
                        arc.round.radius * Math.sin(startAngle),
                        // @ts-ignore
                        arc.round.radius * Math.cos(startAngle),
                        // @ts-ignore
                        arc.round.thickness,
                        0,
                        2 * Math.PI
                    );
                    ctx.arc(
                        // @ts-ignore
                        arc.round.radius * Math.sin(endAngle),
                        // @ts-ignore
                        arc.round.radius * Math.cos(endAngle),
                        // @ts-ignore
                        arc.round.thickness,
                        0,
                        2 * Math.PI
                    );
                    ctx.closePath();
                    ctx.fill();
                    ctx.restore();
                }
            },
        });

        if (this.data) {
            this.updateChart(this.data);
            this.data$ = new BehaviorSubject(this.data);
        } else if (this.data$) {
            this.data$.pipe(takeUntil(this.unsub)).subscribe((data) => this.updateChart(data));
        } else {
            this.ctx.font = '24px Open Sans';
            this.ctx.textAlign = 'center';
            this.ctx.fillText('No data', this.canvas.nativeElement.width / 2, this.canvas.nativeElement.height / 2);
        }
    }

    ngOnDestroy() {
        this.unsub.next();
        this.unsub.complete();

        if (this.chart) {
            this.chart.destroy();
        }
    }

    // reset graph data and labels for redraw
    graphReset() {
        this.graphData.labels = [];
        this.graphData.datasets[0].data = [];
    }

    updateChart(data: GaugeData) {
        if (this.chart) {
            this.chart.destroy();
        }

        // exit function if no data
        // this handles the case where a user switches locations to a location that has no budget data
        if (data === null) {
            return;
        }

        this.graphReset();
        this.rawData = data.values;
        this.graphData.labels = this.labels;
        this.graphData.datasets[0].data = data.values.map((num) => num.toFixed(2));
        this.graphData.datasets[0].backgroundColor[0] = data.color;

        this.chart = new Chart(this.ctx, {
            type: 'doughnut',
            data: this.graphData,
            options: {
                legend: {
                    display: false,
                },
                tooltips: {
                    enabled: false,
                },
                cutoutPercentage: 75,
                responsive: true,
                maintainAspectRatio: false,
                elements: {
                    arc: {
                        // @ts-ignore
                        roundedCornersFor: 0,
                        color: data.color,
                        hoverBackgroundColor: function ({ dataIndex }) {
                            return dataIndex === 0 ? data.color : undefined; // keeps right side orange and underside gray
                        },
                    },
                },
            },
        });
    }
}
